import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AppContext, ManagedHttpContext } from '@osos/shared/types';
import { combineLatest, Observable } from 'rxjs';
import { ConfigService } from '../../config';
import { AppState } from '../../state';

@Injectable()
export class PublicInterceptor implements HttpInterceptor {
  private _appContext: AppContext;
  private _accessId: string;

  constructor(
    private store: Store,
    private configService: ConfigService,
  ) {
    combineLatest([
      this.store.select(AppState.currentAppContext),
      this.store.select(AppState.accessId),
    ]).subscribe(([appContext, accessId]) => {
      this._appContext = appContext;
      this._accessId = accessId;
    });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this._isApiRequest(request.url)) {
      return next.handle(request);
    }

    const httpContext = ManagedHttpContext.create(request.context);

    // This interceptor will work only for public requests:
    if (this._appContext !== AppContext.Public && !httpContext.publicAccessId) {
      return next.handle(request);
    }

    // If the appContext was public, then check if the request url is one of the urlsToIgnore
    if (httpContext.bypassPublicMiddleware) {
      return next.handle(request);
    }

    // Override the accessId by the request's acessId passed by HttpContext if presented:
    const requestAccessId = httpContext.publicAccessId ?? this._accessId;

    // If there is no accessId, then something went wrong, and it should be resolved:
    if (!requestAccessId) {
       
      console.error(
        "Couldn't find the accessId route-param. All the public requests should be done under routes with accessId!",
      );
      return next.handle(request);
    }

    // Delete the root url from the original request to append it to the new request:
    const rootUrl = this.configService.configuration.ACCESS_BASE_URL;
    const urlWithoutRoot = request.url.substring(rootUrl.length);

    // Modify the request url:
    const publicRequest = request.clone({
      url: `${rootUrl}/v1/v/${requestAccessId}${urlWithoutRoot}`,
    });

    return next.handle(publicRequest);
  }

  private _isApiRequest(url: string): boolean {
    return this.configService
      .getApiBaseUrls()
      .some((baseUrl) => url.startsWith(baseUrl));
  }
}
